import type { IDataSourceAdapter } from '../../domains/data-binding/services/adapters/IDataSourceAdapter';
import type { FieldSensitivity } from '../fields/types/FieldManifest';
import type { SourceAuthorityType } from './sourceAuthorityTypes';

/**
 * Folder kind = the specialist domain a folder operates in.
 *
 * Maps 1:1 to `folders.domain` (NULL = 'workspace') — the string stored by
 * the create-folder picker derived from the registered
 * {@link DomainDataProfile.domainId}. Each kind has a fixed set of
 * compatible connectors (filtered via {@link listConnectorsFor}) and a
 * fixed set of panels that mount on the folder dashboard.
 *
 * See plans/compass/folder-federation-architecture.md §Folder kinds.
 */
export type FolderKind =
  | 'workspace'
  | 'orgchart'
  | 'process'
  | 'network'          // Systems Mapping kind — domainId='network'
  | 'genogram'
  | 'software_design'
  | 'strategy'
  | 'grc'
  | 'compass';

/**
 * Where the result of an import lands.
 *
 * `diagram` — hydrate via profile and materialize as nodes on the active canvas
 *             via CreateNodesCommand (undoable, collaborative-safe).
 * `folder`  — emit a Compass change-set into the folder graph for review/accept.
 *             Powers Compass federation, Repository panels, and Q&A.
 * `both`    — folder first, then diagram with shape bindings linked via
 *             folderRecordId. The "land record + draw it" flow.
 */
export type ImportTarget = 'diagram' | 'folder' | 'both';

/**
 * The format the connector accepts at the entry point (drives the picker UI).
 */
export type ConnectorFormat = 'file' | 'oauth' | 'api-key' | 'paste' | 'live-scan';

export type ConnectorStatus = 'stable' | 'beta' | 'coming-soon' | 'hidden';

export type ConnectorSourceSystem =
  | 'csv'
  | 'spreadsheet'
  | 'google_sheets'
  | 'excel'
  | 'azure_ad'
  | 'google_workspace'
  | 'bamboohr'
  | 'workday'
  | 'sop_document'
  | 'aws'
  | 'terraform'
  | 'cloudformation'
  | 'aws_config'
  | 'scanner_report'
  | 'oscal'
  | 'vanta'
  | 'drata'
  | 'rdm'
  | 'code_scanner'
  | 'database'
  | 'adr'
  | 'psa'
  | 'finance'
  | 'niche_system'
  | (string & {});

export type ConnectorSourceVolume = 'low' | 'medium' | 'high';

export type ConnectorSourceRetentionClass =
  | 'current_only'
  | 'rolling_window'
  | 'audit_retained'
  | 'customer_configured'
  | 'legal_hold';

export interface ConnectorSourceSemantics {
  /** Native source system id; used by source runs and mapping profiles. */
  readonly system: ConnectorSourceSystem;
  /** Default ontology/source intent when the connector is self-describing. */
  readonly defaultIntent?: string;
  /** Source intents the adapter and mapping contract have actually demonstrated. */
  readonly supportedIntents?: readonly string[];
  /** Ambiguous sources such as CSV/Sheets/Excel must ask before promotion. */
  readonly requiresIntentResolution?: boolean;
  /** Connector can emit SourceDeltaBatch before observation/promotion. */
  readonly emitsDeltas?: boolean;
  /** Expected source volume; high-volume sources should avoid canvas fanout. */
  readonly volume?: ConnectorSourceVolume;
  readonly retentionClass?: ConnectorSourceRetentionClass;
  /** Coherent fact lanes this connector may participate in; never implies primary authority. */
  readonly authorityTypes?: readonly SourceAuthorityType[];
  /** Highest sensitivity the connector may ingest without an additional reviewed mapping override. */
  readonly maxSensitivity?: FieldSensitivity;
  /** Explicit CRDT safety; absent means source-backed values stay out of Yjs. */
  readonly yjsSafe?: boolean;
  /** Product gates that have been demonstrated for this connector/purpose. */
  readonly enablementGates?: readonly ConnectorEnablementGate[];
}

export type ConnectorIntentEnablement = 'enabled' | 'preview' | 'unsupported';

/** Provider-purpose gate: a connector is enabled only after every required proof has landed. */
export function connectorIntentEnablement(
  manifest: ConnectorManifest,
  intent: string,
): ConnectorIntentEnablement {
  const semantics = manifest.source;
  if (!semantics?.supportedIntents?.includes(intent)) return 'unsupported';
  const proven = new Set(semantics.enablementGates ?? []);
  const required: readonly ConnectorEnablementGate[] = ['contract', 'identity', 'recovery', 'security', 'scale', 'product'];
  return required.every((gate) => proven.has(gate)) ? 'enabled' : 'preview';
}

export type ConnectorEnablementGate =
  | 'contract'
  | 'identity'
  | 'recovery'
  | 'security'
  | 'scale'
  | 'product';

/**
 * Declarative description of one ingest path. Domains register manifests at
 * bootstrap; the picker UI emerges from filtering — exactly mirrors the
 * tool/profile manifest pattern in docs/tools-and-commands-architecture.md.
 *
 * Adding a new connector = register a manifest. No central UI edits.
 */
export interface ConnectorManifest {
  /** Stable unique ID (kebab-case). Duplicate registration throws. */
  readonly id: string;
  /** Human label shown in the picker. */
  readonly label: string;
  /** One-line description shown under the label. */
  readonly description: string;
  /** Asset path or icon component name. */
  readonly iconRef?: string;

  readonly format: ConnectorFormat;
  /** Optional hint string shown in the entry stage (e.g. file name pattern). */
  readonly inputHint?: string;
  /**
   * Optional: the column/field names the parser expects, shown as
   * guidance in the file-upload stage so users know the required shape
   * before picking a file. Most relevant for `format='file'` connectors
   * with a fixed parser (e.g. GRC Register) — `oauth` connectors get an
   * interactive field-mapping step instead, so they rarely need this.
   */
  readonly expectedColumns?: readonly string[];
  /**
   * Optional: downloadable sample files that demonstrate the accepted
   * format. Each `href` is a public asset path (served from `/public`),
   * so a real, parseable example is one click away in the upload stage.
   */
  readonly templates?: readonly {
    readonly label: string;
    readonly href: string;
    /** Optional domain scope for templates on shared connectors such as CSV. */
    readonly folderKinds?: readonly FolderKind[];
  }[];

  /** Folder kinds where this connector should appear. */
  readonly folderKinds: readonly FolderKind[];
  /** Optional restriction by diagram type (only checked when target='diagram'). */
  readonly diagramTypes?: readonly string[];
  /** Which sinks this connector supports. */
  readonly targets: readonly ImportTarget[];

  /** Profiles whose hydrators can consume this connector's output. */
  readonly compatibleProfileIds: readonly string[];

  /** Optional ontology-ingestion metadata for source runs and intent mapping. */
  readonly source?: ConnectorSourceSemantics;

  readonly status: ConnectorStatus;

  /** Optional server-owned sales-interest catalog ID for unavailable connectors. */
  readonly salesInterestId?: string;

  /**
   * Lazy adapter loader. Called when the user actually picks the connector,
   * so adapter modules can stay code-split.
   */
  readonly adapter: () => Promise<IDataSourceAdapter>;
}

const connectorRegistry = new Map<string, ConnectorManifest>();

/**
 * Register a connector manifest. Throws on duplicate ID (defensive — better
 * to fail at bootstrap than to silently mask one connector with another).
 *
 * Profile-ID validation happens at picker time, not registration time, so
 * connectors and profiles can register in any order.
 */
export function registerConnector(manifest: ConnectorManifest): void {
  if (!manifest.id) {
    throw new Error('[ConnectorRegistry] manifest is missing id');
  }
  if (manifest.folderKinds.length === 0) {
    throw new Error(`[ConnectorRegistry] manifest "${manifest.id}" must declare at least one folderKind`);
  }
  if (manifest.targets.length === 0) {
    throw new Error(`[ConnectorRegistry] manifest "${manifest.id}" must declare at least one target`);
  }
  const existing = connectorRegistry.get(manifest.id);
  if (existing) {
    throw new Error(`[ConnectorRegistry] duplicate connector id: "${manifest.id}"`);
  }
  connectorRegistry.set(manifest.id, manifest);
}

/**
 * List connectors offered for a given folder kind, optionally narrowed by
 * the active diagram type. Hidden connectors are always excluded.
 */
export function listConnectorsFor(
  folderKind: FolderKind,
  diagramType?: string,
): ConnectorManifest[] {
  const out: ConnectorManifest[] = [];
  for (const manifest of connectorRegistry.values()) {
    if (manifest.status === 'hidden') continue;
    if (!manifest.folderKinds.includes(folderKind)) continue;
    if (
      diagramType &&
      manifest.diagramTypes &&
      manifest.diagramTypes.length > 0 &&
      !manifest.diagramTypes.includes(diagramType)
    ) {
      continue;
    }
    out.push(manifest);
  }
  return out;
}

/**
 * Subset of {@link listConnectorsFor} filtered by sink target.
 */
export function listConnectorsForTarget(
  folderKind: FolderKind,
  target: ImportTarget,
  diagramType?: string,
): ConnectorManifest[] {
  return listConnectorsFor(folderKind, diagramType).filter((m) => m.targets.includes(target));
}

export function getConnector(id: string): ConnectorManifest | null {
  return connectorRegistry.get(id) ?? null;
}

export function listAllConnectors(): ConnectorManifest[] {
  return Array.from(connectorRegistry.values());
}

/** Test-only: clears all registered connectors. */
export function resetConnectorRegistry(): void {
  connectorRegistry.clear();
}

export const SOP_DOCUMENT_CONNECTOR_ID = 'sop-document';

export function filterConnectorsForProcessExtractionUx(
  connectors: ConnectorManifest[],
  showProcessExtractionUx: boolean,
): ConnectorManifest[] {
  if (showProcessExtractionUx) {
    return connectors;
  }
  return connectors.filter((connector) => connector.id !== SOP_DOCUMENT_CONNECTOR_ID);
}

/**
 * The Blueprint flag gates the *network surface*, not every connector that
 * happens to serve it. Shared connectors (`csv-upload`) declare `network`
 * alongside `orgchart`/`process`, so filtering on manifest capability removed
 * CSV from Atlas and Process whenever Blueprint was off. Scope the filter to
 * the surface actually being rendered; an unknown surface keeps the
 * conservative behaviour.
 */
export function filterConnectorsForBlueprint(
  connectors: ConnectorManifest[],
  blueprintEnabled: boolean,
  folderKind?: FolderKind,
): ConnectorManifest[] {
  if (blueprintEnabled) {
    return connectors;
  }
  if (folderKind && folderKind !== 'network') {
    return connectors;
  }
  return connectors.filter((connector) => !connector.folderKinds.includes('network'));
}

export function filterConnectorsForRdmImportExport(
  connectors: ConnectorManifest[],
  rdmImportExportEnabled: boolean,
): ConnectorManifest[] {
  if (rdmImportExportEnabled) {
    return connectors;
  }
  return connectors.filter((connector) => connector.id !== 'rdm-import');
}

export function applyFeatureFlagConnectorFilters(
  connectors: ConnectorManifest[],
  options: {
    folderKind?: FolderKind;
    showProcessExtractionUx: boolean;
    blueprintEnabled: boolean;
    rdmImportExportEnabled: boolean;
  },
): ConnectorManifest[] {
  let filtered = connectors;
  filtered = filterConnectorsForBlueprint(filtered, options.blueprintEnabled, options.folderKind);
  filtered = filterConnectorsForRdmImportExport(filtered, options.rdmImportExportEnabled);
  if (!options.folderKind || options.folderKind === 'process') {
    filtered = filterConnectorsForProcessExtractionUx(filtered, options.showProcessExtractionUx);
  }
  return filtered;
}
